import React, { useCallback, useEffect, useId, type FormEvent } from "react";
import { useLocalization, LocalizationKeys } from "../../../i18n";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useToggle } from "../../../hooks/useToggle";
import { useSearchState } from "./hooks/useSearchState";
import { DesktopSearch } from "./desktopSearch/desktopSearch";
import { MobileSearch } from "./mobileSearch/mobileSearch";
import { MobileSearchOverlay } from "./mobileSearchOverlay/mobileSearchOverlay";
import type { ISearchCategory } from "../../../constants/searchCategory";
import styles from "./search.module.css";

export function Search() {
  const isMobile = useMediaQuery("(max-width: 595px)");
  const {
    isOpen: isOverlayOpen,
    open: openOverlay,
    close: closeOverlay,
  } = useToggle(false);
  const { t } = useLocalization();
  const searchInputId = useId();

  const {
    query,
    setQuery,
    selectedCategory,
    setSelectedCategory,
    searchResults,
    isSearching,
    searchError,
    clearSearch,
  } = useSearchState();

  // Закрываем overlay при переходе на десктоп
  useEffect(() => {
    if (!isMobile && isOverlayOpen) {
      closeOverlay();
    }
  }, [isMobile, isOverlayOpen, closeOverlay]);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
    },
    [setQuery],
  );

  const handleCategorySelect = useCallback(
    (category: ISearchCategory) => {
      setSelectedCategory(category);
    },
    [setSelectedCategory],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isMobile) {
        closeOverlay();
      }
    },
    [closeOverlay, isMobile],
  );

  const handleAfterNavigate = useCallback(() => {
    if (isMobile) {
      closeOverlay();
    }
    clearSearch();
  }, [isMobile, closeOverlay, clearSearch]);

  const searchContentProps = {
    selectedCategory,
    onSelectCategory: handleCategorySelect,
    query,
    onQueryChange: handleQueryChange,
    onAfterNavigate: handleAfterNavigate,
    onSubmit: handleSubmit,
    inputId: searchInputId,
    searchPlaceholder: t(LocalizationKeys.HEADER_SEARCH),
    searchResults,
    isSearching,
    searchError,
  };

  return (
    <React.Fragment>
      <div className={styles.searchComponent}>
        {!isMobile ? (
          <DesktopSearch {...searchContentProps} />
        ) : (
          <MobileSearch onOpenSearch={openOverlay} />
        )}
      </div>

      <MobileSearchOverlay
        {...searchContentProps}
        isOpen={isMobile && isOverlayOpen}
        onClose={closeOverlay}
      />
    </React.Fragment>
  );
}
